import { Button } from '@skytech/ui/Button'
import { ExternalLink } from '@skytech/ui/ExternalLink'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import DownloadIcon from 'mdi-react/DownloadIcon'
import { useSelector } from 'react-redux'

import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'
import subscriptionOrderResources from '@/components/subscription-orders/resources'
import { No, Yes } from '@/components/util/Polars'
import { getSubscriptionOrder } from '@/ducks/subscriptionOrderReducer'
import { FORM_URL } from '@/util/form'
import { getFormatterMobileNumber } from '@/util/formatters/text'
import { WAIVER_URL_OLD } from '@/util/misc'
import { isOrderOfType, SUBSCRIPTION_ORDER_STATUS, SUBSCRIPTION_ORDER_TYPES } from '@/util/orders'

const OLD_FORM_MAX_ID = 5560

const SubscriptionOrderDetails = () => {
  const { whiteLabel } = useAppContext()
  const order = useSelector(getSubscriptionOrder)

  const {
    user,
    last_edited_by,
    order_update,
    is_new_number,
    name_company,
    birth_date_org_no,
    personal_id,
    contact_number,
    sim_name,
    sim_adr,
    sim_zip,
    sim_city,
    sim_number,
    web_key,
    terminationTypeText,
    termination_date,
    credit_check_acceptance_given,
    subscription_service_name,
    price,
    status,
    id,
    document_id,
    currency,
    is_esim,
    api_order_id,
    api_order_date,
    api_supplier_order_status,
    api_supplier_order_status_reason,
    api_order_sync_result,
    eid,
  } = order

  const { refetch: downloadWaiver, isInitialLoading: isDownloading } = useQuery(
    queries.subscriptionOrderWaiver(web_key),
    () => subscriptionOrderResources.getWaiver(web_key),
    {
      enabled: false,
    }
  )

  return (
    <section>
      <h3>
        <Translation id="Order" />
      </h3>
      <DataPoints>
        {isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.UPGRADE) && (
          <>
            <DataPoint
              header={<Translation id="Product" />}
              value={subscription_service_name || '—'}
            />
            <DataPoint
              header={<Translation id="Price" />}
              value={<FormattedNumberWithCurrency amount={price} currency={currency} />}
            />
          </>
        )}

        {isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.TERMINATION) && (
          <>
            <DataPoint
              header={<Translation id="Form" />}
              value={
                <ExternalLink withIcon href={FORM_URL.replace(':code', web_key)}>
                  <Translation id="View" />
                </ExternalLink>
              }
            />

            <DataPoint header={<Translation id="Type" />} value={terminationTypeText || '—'} />

            <DataPoint
              header={<Translation id="Termination date" />}
              value={<LocalDate date={termination_date} />}
            />

            <DataPoint
              header={<Translation id="Credit check acceptance" />}
              value={credit_check_acceptance_given ? <Yes /> : <No />}
            />

            <DataPoint
              header={<Translation id="Birth date/ org. nbr." />}
              value={birth_date_org_no || '—'}
            />
          </>
        )}

        {isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.PORTING) && (
          <>
            <DataPoint
              className="documents-section"
              header={<Translation id="Form" />}
              value={
                <>
                  <ExternalLink withIcon href={FORM_URL.replace(':code', web_key)}>
                    <Translation id="View" />
                  </ExternalLink>

                  {status !== SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER &&
                    id <= OLD_FORM_MAX_ID && (
                      <ExternalLink
                        withIcon
                        className="mt-3"
                        href={WAIVER_URL_OLD.replace(':code', web_key)}
                      >
                        pdf
                      </ExternalLink>
                    )}

                  {Boolean(document_id) && (
                    <Button
                      isIconRight
                      variant="tertiary"
                      className="m-0 p-0"
                      Icon={<DownloadIcon />}
                      isLoading={isDownloading}
                      onClick={() => downloadWaiver()}
                      whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    >
                      <Translation id="Waiver" />
                    </Button>
                  )}
                </>
              }
            />

            <DataPoint
              header={<Translation id="Contact number" />}
              value={contact_number ? getFormatterMobileNumber(contact_number) : '—'}
            />

            <DataPoint
              header={<Translation id="New number" />}
              value={is_new_number ? <Yes /> : <No />}
            />

            {/* Hiding for now till we implement this on API */}
            {/* <DataPoint
              header={<Translation id="New twin SIM" />}
              value={has_new_twin_sim ? <Yes /> : <No />}
            /> */}

            <DataPoint header={<Translation id="Name/ company" />} value={name_company || '—'} />

            <DataPoint
              header={<Translation id="Birth date/ org. nbr." />}
              value={birth_date_org_no || personal_id || '—'}
            />

            <DataPoint header={<Translation id="SIM receiver" />} value={sim_name || '—'} />
          </>
        )}

        <DataPoint
          header={<Translation id="SIM address" />}
          value={
            sim_adr ? (
              <>
                {sim_adr}
                <br />
                {sim_zip}, {sim_city}
              </>
            ) : (
              '—'
            )
          }
        />

        <DataPoint header={<Translation id="SIM number" />} value={sim_number || '—'} />

        <>
          <DataPoint
            header={<Translation id="eSim" />}
            value={<Translation id={is_esim ? 'Yes' : 'No'} />}
          />

          <DataPoint
            header={<Translation id="EID" className="uppercase" />}
            value={eid || '—'}
            className="break-all"
          />
        </>

        <DataPoint
          header={<Translation id="Last update" />}
          value={<LocalDateTime date={order_update} />}
        />

        <DataPoint header={<Translation id="Last update by" />} value={last_edited_by || user} />

        {api_order_id && api_order_date && (
          <DataPoint
            header={<Translation id="Carrier order" />}
            value={
              <>
                <Translation id="ID" />: {api_order_id}, <Translation id="Date" />:{' '}
                <LocalDateTime date={api_order_date} />
              </>
            }
          />
        )}
        {api_supplier_order_status && (
          <DataPoint
            header={<Translation id="Carrier status" />}
            value={
              <>
                <Translation id={`API_SUPPLIER_ORDER_STATUS/${api_supplier_order_status}`} />
                <br />
                {api_supplier_order_status_reason && api_supplier_order_status_reason}
                <br />
                {api_order_sync_result && api_order_sync_result}
              </>
            }
          />
        )}
      </DataPoints>
    </section>
  )
}

export default SubscriptionOrderDetails
